/**
 * Data Fetching Optimization Utilities
 * Implements advanced caching, request batching, and performance monitoring
 */

import { enhancedQuery } from "../lib/supabase";
import { getCachedData, setCachedData } from "../lib/cache";

// Request batching for multiple similar requests
interface BatchRequest<T> {
  id: string;
  promise: Promise<T>;
  resolve: (value: T) => void;
  reject: (error: any) => void;
}

class RequestBatcher {
  private batches = new Map<string, BatchRequest<any>[]>();
  private batchTimeouts = new Map<string, NodeJS.Timeout>();

  /**
   * Batch multiple requests of the same type
   */
  async batchRequest<T>(
    batchKey: string,
    requestId: string,
    requestFn: () => Promise<T>,
    batchDelay: number = 100,
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      const batchRequest: BatchRequest<T> = {
        id: requestId,
        promise: requestFn(),
        resolve,
        reject,
      };

      // Add to existing batch or create new one
      if (!this.batches.has(batchKey)) {
        this.batches.set(batchKey, []);
      }
      this.batches.get(batchKey)!.push(batchRequest);

      // Set timeout to execute batch
      if (!this.batchTimeouts.has(batchKey)) {
        const timeout = setTimeout(() => {
          this.executeBatch(batchKey);
        }, batchDelay);
        this.batchTimeouts.set(batchKey, timeout);
      }
    });
  }

  /**
   * Execute a batch of requests
   */
  private async executeBatch(batchKey: string) {
    const batch = this.batches.get(batchKey);
    if (!batch) return;

    this.batches.delete(batchKey);
    this.batchTimeouts.delete(batchKey);

    // Execute all requests in parallel
    const promises = batch.map((req) => req.promise);

    try {
      const results = await Promise.all(promises);

      // Resolve all requests with their respective results
      batch.forEach((req, index) => {
        req.resolve(results[index]);
      });
    } catch (error) {
      // Reject all requests if any fail
      batch.forEach((req) => {
        req.reject(error);
      });
    }
  }
}

// Global request batcher instance
export const requestBatcher = new RequestBatcher();

// Intelligent caching with cache warming
export class IntelligentCache {
  private cacheWarmupQueue: Array<() => Promise<void>> = [];
  private isWarmingUp = false;

  /**
   * Get data with intelligent caching strategy
   */
  async getWithCache<T>(
    key: string,
    fetchFn: () => Promise<T>,
    options: {
      ttl?: number;
      warmup?: boolean;
      staleWhileRevalidate?: boolean;
    } = {},
  ): Promise<T> {
    const {
      ttl = 300000,
      warmup = false,
      staleWhileRevalidate = true,
    } = options;

    // Check cache first
    const cached = getCachedData<T>(key);
    if (cached) {
      // If stale-while-revalidate is enabled, fetch fresh data in background
      if (staleWhileRevalidate) {
        this.warmupCache(key, fetchFn, ttl);
      }
      return cached;
    }

    // Fetch fresh data
    const data = await fetchFn();
    setCachedData(key, data, ttl);
    return data;
  }

  /**
   * Warm up cache in background
   */
  private async warmupCache<T>(
    key: string,
    fetchFn: () => Promise<T>,
    ttl: number,
  ): Promise<void> {
    try {
      const data = await fetchFn();
      setCachedData(key, data, ttl);
      console.log(`🔥 Cache warmed up for: ${key}`);
    } catch (error) {
      console.warn(`Failed to warm up cache for ${key}:`, error);
    }
  }

  /**
   * Queue cache warmup for later execution
   */
  queueWarmup<T>(key: string, fetchFn: () => Promise<T>, ttl: number): void {
    this.cacheWarmupQueue.push(() => this.warmupCache(key, fetchFn, ttl));

    if (!this.isWarmingUp) {
      this.processWarmupQueue();
    }
  }

  /**
   * Process warmup queue
   */
  private async processWarmupQueue(): Promise<void> {
    if (this.isWarmingUp || this.cacheWarmupQueue.length === 0) return;

    this.isWarmingUp = true;

    while (this.cacheWarmupQueue.length > 0) {
      const warmupFn = this.cacheWarmupQueue.shift();
      if (warmupFn) {
        await warmupFn();
        // Small delay between warmups to avoid overwhelming the server
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
    }

    this.isWarmingUp = false;
  }
}

// Global intelligent cache instance
export const intelligentCache = new IntelligentCache();

// Performance monitoring for data fetching
export const trackDataFetch = (operation: string, startTime: number) => {
  const duration = performance.now() - startTime;

  console.log(`📊 Data fetch: ${operation} took ${duration.toFixed(2)}ms`);

  // Send to analytics if available
  if (typeof window !== "undefined" && (window as any).gtag) {
    (window as any).gtag("event", "data_fetch", {
      operation,
      duration: Math.round(duration),
    });
  }

  // Alert on slow requests
  if (duration > 2000) {
    console.warn(
      `🐌 Slow data fetch detected: ${operation} (${duration.toFixed(2)}ms)`,
    );
  }
};

// Optimized data fetching wrapper
export const optimizedFetch = async <T>(
  operation: string,
  fetchFn: () => Promise<T>,
  options: {
    cacheKey?: string;
    ttl?: number;
    batchKey?: string;
    requestId?: string;
    warmup?: boolean;
  } = {},
): Promise<T> => {
  const startTime = performance.now();
  const {
    cacheKey,
    ttl = 300000,
    batchKey,
    requestId,
    warmup = false,
  } = options;

  try {
    let result: T;

    // Use batching if specified
    if (batchKey && requestId) {
      result = await requestBatcher.batchRequest(batchKey, requestId, fetchFn);
    } else {
      // Use intelligent caching if cache key provided
      if (cacheKey) {
        result = await intelligentCache.getWithCache(cacheKey, fetchFn, {
          ttl,
          warmup,
        });
      } else {
        // Direct fetch
        result = await fetchFn();
      }
    }

    trackDataFetch(operation, startTime);
    return result;
  } catch (error) {
    trackDataFetch(`${operation}_error`, startTime);
    throw error;
  }
};

// Prefetch utilities for better UX
export const prefetchData = {
  /**
   * Prefetch blog posts for better navigation
   */
  blogPosts: async () => {
    const cacheKey = "prefetch_blog_posts";
    if (!getCachedData(cacheKey)) {
      intelligentCache.queueWarmup(
        cacheKey,
        async () => {
          const { data } = await enhancedQuery(
            async (signal) =>
              supabase
                .from("blog_posts")
                .select("slug, title, excerpt, created_at")
                .eq("is_published", true)
                .order("created_at", { ascending: false })
                .limit(10)
                .abortSignal(signal),
            { cacheKey: "prefetch_blog_posts_query" },
          );
          return data;
        },
        600000, // 10 minutes
      );
    }
  },

  /**
   * Prefetch course data
   */
  courses: async () => {
    const cacheKey = "prefetch_courses";
    if (!getCachedData(cacheKey)) {
      intelligentCache.queueWarmup(
        cacheKey,
        async () => {
          const { data } = await enhancedQuery(
            async (signal) =>
              supabase
                .from("courses")
                .select("id, title, description, price")
                .eq("is_published", true)
                .eq("deleted", false)
                .order("created_at", { ascending: false })
                .abortSignal(signal),
            { cacheKey: "prefetch_courses_query" },
          );
          return data;
        },
        600000, // 10 minutes
      );
    }
  },
};

// Export supabase for use in this file
import { supabase } from "../lib/supabase";
